/** Return an exported hinge's correction paint to its chain before rebuilding.
 * Depends on three/kernel; consumed only by explicit Document hinge rebuilds.
 * Original slots lost to historical eviction are unknowable; count reconstruction.
 */
import { Vector3, type SkinnedMesh } from 'three';
import type { ResolvedHinge, RigDescriptor } from '@harryhausen/kernel';

export function clearHingeCorrection(mesh: SkinnedMesh, rig: RigDescriptor, hinge: ResolvedHinge) {
  const index = mesh.geometry.getAttribute('skinIndex');
  const weight = mesh.geometry.getAttribute('skinWeight');
  const position = mesh.geometry.getAttribute('position');
  const names = mesh.skeleton.bones.map((b) => b.name);
  const correction = new Set([hinge.helper, ...hinge.leaves.map((l) => l.name)]);
  const isChain = (name: string) =>
    name === hinge.parent ||
    name === hinge.driver ||
    name.startsWith(`${hinge.parent}Twist`) ||
    name.startsWith(`${hinge.driver}Twist`);
  const parentSlot = names.indexOf(hinge.parent),
    driverSlot = names.indexOf(hinge.driver);
  if (parentSlot < 0 || driverSlot < 0)
    throw new Error(`Cannot rebuild ${hinge.helper}: arm chain missing`);
  const joint = new Vector3().copy(
    rig.bones.find((b) => b.name === hinge.driver)!.worldBind.position,
  );
  const axis = new Vector3()
    .copy(rig.bones.find((b) => b.name === hinge.end)!.worldBind.position)
    .sub(joint)
    .normalize();
  let vertices = 0,
    reconstructed = 0;
  for (let v = 0; v < position.count; v++) {
    const slots = new Map<number, number>();
    let removed = 0,
      residual = 0;
    for (let k = 0; k < weight.itemSize; k++) {
      const slot = index.getComponent(v, k),
        w = weight.getComponent(v, k);
      if (w <= 0) continue;
      if (correction.has(names[slot]!)) removed += w;
      else {
        slots.set(slot, (slots.get(slot) ?? 0) + w);
        if (isChain(names[slot]!)) residual += w;
      }
    }
    if (removed === 0) continue;
    vertices++;
    if (residual > 1e-7) {
      for (const [slot, w] of slots)
        if (isChain(names[slot]!)) slots.set(slot, w * (1 + removed / residual));
    } else {
      // The old band took all chain weight. Recover a bounded axial blend;
      // never claim this is the artist's unknown original distribution.
      reconstructed++;
      const p = new Vector3().fromBufferAttribute(position, v).applyMatrix4(mesh.bindMatrix);
      const distal = Math.max(0, Math.min(1, 0.5 + p.sub(joint).dot(axis) / (2 * hinge.bandWorld)));
      slots.set(parentSlot, (slots.get(parentSlot) ?? 0) + removed * (1 - distal));
      slots.set(driverSlot, (slots.get(driverSlot) ?? 0) + removed * distal);
    }
    const entries = [...slots].filter(([, w]) => w > 0).sort((a, b) => b[1] - a[1]);
    if (entries.length > weight.itemSize)
      throw new Error(
        `Cannot rebuild ${hinge.helper}: reconstructed vertex ${v} exceeds skin slots`,
      );
    for (let k = 0; k < weight.itemSize; k++) {
      index.setComponent(v, k, entries[k]?.[0] ?? 0);
      weight.setComponent(v, k, entries[k]?.[1] ?? 0);
    }
  }
  index.needsUpdate = true;
  weight.needsUpdate = true;
  const remove = new Set(
    hinge.profile.morphs.flatMap((m) => {
      const i = mesh.morphTargetDictionary?.[m.name];
      return i === undefined ? [] : [i];
    }),
  );
  const oldNames = new Map(
    Object.entries(mesh.morphTargetDictionary ?? {}).map(([n, i]) => [i, n]),
  );
  const oldWeights = [...(mesh.morphTargetInfluences ?? [])];
  for (const channel of ['position', 'normal', 'color'] as const) {
    const attrs = mesh.geometry.morphAttributes[channel];
    if (attrs) mesh.geometry.morphAttributes[channel] = attrs.filter((_, i) => !remove.has(i));
  }
  mesh.updateMorphTargets();
  const kept = oldWeights.flatMap((w, i) => (remove.has(i) ? [] : [{ w, name: oldNames.get(i) }]));
  mesh.morphTargetDictionary = {};
  kept.forEach((entry, i) => {
    if (entry.name !== undefined) mesh.morphTargetDictionary![entry.name] = i;
    mesh.morphTargetInfluences![i] = entry.w;
  });
  return { vertices, reconstructed };
}
